import React, { useContext, useRef, useState } from 'react';
import { EngineContext } from '@philips-internal/oswald-ui-engine';
import { PreviewActionType } from '../../actions/preview-actions.js';
import { useApplicationPreviewActions } from '../../hooks/use-application-preview-actions.js';
import { usePreviewWebSocket } from '../../hooks/use-preview-websocket.js';
import { ContextMenu } from '../context-menu/context-menu.js';
import { updateNode, updateNodeChildren } from './preview-selectable-utils.js';
import './preview-selectable.css';
/**
 * Create a wrapper component that enhances a given component with preview selection behavior and functionality.
 *
 * The resulting wrapper component can be used to render the enhanced component with additional features,
 * such as context menu actions and WebSocket communication.
 *
 * @param {CreatePreviewSelectableParameters} parameters - The parameters for creating the wrapper component.
 */
function createPreviewSelectable(parameters) {
    const { node, propTypes, component: WrappedComponent } = parameters;
    /**
     * The wrapper component that enhances the provided component with preview selection behavior and functionality.
     *
     * @param {PreviewSelectableProps} props - Properties for the wrapper component.
     */
    function PreviewSelectable(props) {
        const { engine, root } = useContext(EngineContext);
        const fragment = root?.getName() ?? '';
        const contextMenuTargetRef = useRef(null);
        const actions = useRef(useApplicationPreviewActions({
            fragment,
            node: props.uuid,
        }));
        const { children: childrenFromProps, ...otherProps } = props;
        const [children, setChildren] = useState(childrenFromProps);
        const onMessage = (event) => {
            if (!engine || !root) {
                return;
            }
            const data = JSON.parse(event.data);
            if (data.type !== PreviewActionType.UPDATE_NODE && data.type !== PreviewActionType.UPDATE_NODE_CHILDREN || data.node !== props.uuid) {
                return;
            }
            if (data.type === PreviewActionType.UPDATE_NODE) {
                return updateNode({ ...data, root, uuid: props.uuid });
            }
            if (data.type === PreviewActionType.UPDATE_NODE_CHILDREN) {
                return updateNodeChildren({ ...data, engine, root, setChildren });
            }
        };
        usePreviewWebSocket({
            onMessage,
        });
        return (React.createElement(ContextMenu, { actions: actions.current, targetRef: contextMenuTargetRef },
            React.createElement("div", { className: "c-preview-selectable u-hide@empty" },
                React.createElement("div", { className: "c-preview-selectable__popover-target", ref: contextMenuTargetRef }),
                React.createElement(WrappedComponent, { ...otherProps }, children))));
    }
    Object.defineProperty(PreviewSelectable, 'name', { value: `${node.type}[PreviewSelectable]`, writable: false });
    Object.defineProperty(PreviewSelectable, 'propTypes', { value: propTypes, writable: false });
    return PreviewSelectable;
}
export { createPreviewSelectable, };
//# sourceMappingURL=preview-selectable.js.map